import { warn } from '../../../logger';
import { InternalElementFactoryOptions, SdkElement } from '../../../model';
import { recordEvent } from '../../../telemetry';
import { ENDPOINTS } from '../../endpoints';
import { SDKError } from '../../error';
import { PaymentElement, PaymentElementOptions } from './PaymentElement';
import { parsePaymentElementOptions } from './optionsSchema';

const createWithValidation = async <T extends typeof PaymentElement>(
  options: PaymentElementOptions,
  element: T
): Promise<SdkElement> => {
  const { sessionId } = options;

  const response = await window.fetch(`${ENDPOINTS.fetchSession}/${sessionId}`, {
    method: 'GET'
  });

  if (!response.ok) {
    throw new SDKError('invalidSessionId');
  }

  return element(options as Parameters<T>[0]);
};

export const createPaymentElement = async (
  elementOptions: PaymentElementOptions,
  commonOptions: InternalElementFactoryOptions
): Promise<SdkElement> => {
  await recordEvent(
    'sdk_payment_element_init_options',
    { payment_element_options: { ...elementOptions }, elements_options: commonOptions },
    elementOptions.sessionId
  );

  const {
    errors,
    warnings,
    options: validatedOptions,
    initialOptions
  } = parsePaymentElementOptions(elementOptions);

  if (errors) {
    throw new SDKError(
      'invalidPaymentElementOptions',
      'Invalid Payment Element options provided. See docs: <docs-here>',
      errors
    );
  }

  if (warnings) {
    warn('invalidPaymentElementOptions', warnings);
  }

  await recordEvent(
    'sdk_payment_element_options_validated',
    {
      payment_element_warnings: warnings,
      payment_element_options: initialOptions,
      elements_options: commonOptions
    },
    elementOptions.sessionId
  );

  return createWithValidation({ ...validatedOptions, ...commonOptions }, PaymentElement);
};
